import React, { useRef, useEffect } from "react";
import { Box, Typography, Alert } from "@mui/material";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  Polygon,
  FeatureGroup,
  useMap,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useTranslate } from "@refinedev/core";
import { ZoneType } from "./ZoneTypeSelector";
import { ShapeType } from "./ShapeTypeSelector";

// Fix for Leaflet icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export interface PolygonCoordinate {
  lat: number;
  lng: number;
}

interface PolygonDrawMapProps {
  position: [number, number];
  setPosition: (position: [number, number]) => void;
  radius: number;
  shapeType: ShapeType;
  zoneType: ZoneType;
  polygon: PolygonCoordinate[];
  onPolygonChange: (polygon: PolygonCoordinate[]) => void;
  height?: string;
}

// Component to handle map view updates
function MapViewController({
  position,
  zoom,
}: {
  position: [number, number];
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (position && map) {
      map.setView(position, zoom);
    }
  }, [position, zoom, map]);

  return null;
}

// Component for circle mode (draggable marker + circle)
function CircleMarker({
  position,
  setPosition,
  radius,
  color,
}: {
  position: [number, number];
  setPosition: (position: [number, number]) => void;
  radius: number;
  color: string;
}) {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const latlng = marker.getLatLng();
        setPosition([latlng.lat, latlng.lng]);
      }
    },
  };

  return (
    <>
      <Marker
        position={position}
        draggable
        ref={markerRef}
        eventHandlers={eventHandlers}
      />
      <Circle
        center={position}
        radius={radius}
        pathOptions={{
          color: color,
          fillColor: color,
          fillOpacity: 0.2,
        }}
      />
    </>
  );
}

/**
 * PolygonDrawMap - Map component for drawing zones
 *
 * Supports two modes:
 * - CIRCLE: Draggable marker with radius circle
 * - POLYGON: Drawing tool for custom polygon shapes
 *
 * Colors change based on zone type:
 * - AUTHORIZED: Blue
 * - FORBIDDEN: Red
 */
const PolygonDrawMap: React.FC<PolygonDrawMapProps> = ({
  position,
  setPosition,
  radius,
  shapeType,
  zoneType,
  polygon,
  onPolygonChange,
  height = "500px",
}) => {
  const translate = useTranslate();
  const featureGroupRef = useRef<L.FeatureGroup>(null);

  // Color based on zone type
  const zoneColor = zoneType === "FORBIDDEN" ? "#d32f2f" : "#1976d2";

  // Calculate zoom based on radius
  const zoom = radius > 500 ? 13 : radius > 200 ? 14 : 15;

  // Handle polygon creation
  const handleCreated = (e: any) => {
    const layer = e.layer;
    if (layer instanceof L.Polygon) {
      const latlngs = layer.getLatLngs()[0] as L.LatLng[];
      const coordinates: PolygonCoordinate[] = latlngs.map((ll) => ({
        lat: ll.lat,
        lng: ll.lng,
      }));
      onPolygonChange(coordinates);

      // Calculate and set centroid as position
      const sumLat = coordinates.reduce((sum, c) => sum + c.lat, 0);
      const sumLng = coordinates.reduce((sum, c) => sum + c.lng, 0);
      setPosition([sumLat / coordinates.length, sumLng / coordinates.length]);

      // Clear the drawn layer (we render our own Polygon component)
      featureGroupRef.current?.clearLayers();
    }
  };

  // Handle polygon edit
  const handleEdited = (e: any) => {
    const layers = e.layers;
    layers.eachLayer((layer: any) => {
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        const coordinates: PolygonCoordinate[] = latlngs.map((ll) => ({
          lat: ll.lat,
          lng: ll.lng,
        }));
        onPolygonChange(coordinates);

        // Update centroid
        const sumLat = coordinates.reduce((sum, c) => sum + c.lat, 0);
        const sumLng = coordinates.reduce((sum, c) => sum + c.lng, 0);
        setPosition([sumLat / coordinates.length, sumLng / coordinates.length]);
      }
    });
  };

  // Handle polygon delete
  const handleDeleted = () => {
    onPolygonChange([]);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {shapeType === "POLYGON" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate("sharedPlaces.form.polygonInstructions")}
        </Alert>
      )}
      <Box
        sx={{
          height,
          width: "100%",
          borderRadius: "6px",
          overflow: "hidden",
          border: `2px solid ${zoneColor}`,
        }}
      >
        <MapContainer
          center={position}
          zoom={zoom}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewController position={position} zoom={zoom} />

          {shapeType === "CIRCLE" ? (
            <CircleMarker
              position={position}
              setPosition={setPosition}
              radius={radius}
              color={zoneColor}
            />
          ) : (
            <>
              <FeatureGroup ref={featureGroupRef}>
                <EditControl
                  position="topright"
                  onCreated={handleCreated}
                  onEdited={handleEdited}
                  onDeleted={handleDeleted}
                  draw={{
                    rectangle: false,
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polyline: false,
                    polygon: {
                      allowIntersection: false,
                      shapeOptions: {
                        color: zoneColor,
                        fillColor: zoneColor,
                        fillOpacity: 0.2,
                      },
                    },
                  }}
                />
              </FeatureGroup>
              {polygon.length >= 3 && (
                <Polygon
                  positions={polygon.map((p) => [p.lat, p.lng])}
                  pathOptions={{
                    color: zoneColor,
                    fillColor: zoneColor,
                    fillOpacity: 0.2,
                  }}
                />
              )}
            </>
          )}
        </MapContainer>
      </Box>
      {shapeType === "POLYGON" && polygon.length >= 3 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {translate("sharedPlaces.form.polygonPoints", {
            count: polygon.length,
          })}
        </Typography>
      )}
    </Box>
  );
};

export default PolygonDrawMap;
